import { useEffect } from 'react'
import { message } from 'antd'
import VerifyEmail from '../components/Authentication/VerifyEmail'
import {
  useFirestore,
  useFirestoreDocData,
  useFunctions,
  useSigninCheck,
} from 'reactfire'
import { doc } from 'firebase/firestore'
import App from '../App'
import { useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { sendVerificationEmail } from '../lib/sendVerificationEmail'
import { errorResponse } from '../lib/errorResponse'
import * as Sentry from '@sentry/react'
import { markAuthReady } from '../lib/firestoreIncompleteKeyContext'
import i18next from '../lib/i18n'
import { useApplyProfileLanguage } from '../lib/localeController'
import { useI18nEnabled } from '../lib/useI18nEnabled'
import { useApplyProfileTheme } from '../lib/themeController'

// Additional local check against sending multiple emails
// if the profile hasn't been updated yet
let emailVerificationSent = false

export default function SignedIn() {
  const firestore = useFirestore()
  const functions = useFunctions()
  const { data: signInCheck } = useSigninCheck()

  const { status: profileStatus, data: profile } = useFirestoreDocData(
    doc(firestore, 'userprofiles', signInCheck.user.uid),
    { idField: 'uid' }
  )

  const [searchParams] = useSearchParams()
  const englishEnabled = useI18nEnabled()

  // Apply the account's stored language preference (CL-1551 / i18n F4); kept
  // session-local and skipped during impersonation (CL-2059 / CL-2062), see
  // the hook's doc.
  useApplyProfileLanguage({
    profileStatus,
    language: profile?.language,
    englishEnabled,
  })

  // Same for the stored colour scheme. A tab that loads hidden has Firestore
  // offline and gets an empty profile from cache; that is not a preference.
  useApplyProfileTheme({
    profileLoaded:
      profileStatus === 'success' && profile?.acceptedTerms !== undefined,
    theme: profile?.theme,
  })

  // Set Sentry user context for error tracking
  useEffect(() => {
    if (!signInCheck?.user?.uid) return undefined

    Sentry.setUser({
      id: signInCheck.user.uid,
    })
    markAuthReady()

    // Record impersonation state so permission-denied / "insufficient rights"
    // errors are attributable to impersonation (the client shows privileged
    // chrome via impersonator_admin while the server strips powers) vs a stale
    // client state after a claim change. The impersonated user has a different
    // uid, so this effect re-runs and the tag stays accurate across
    // start/stop. CL-WEBAPP-J2 / CL-WEBAPP-MD.
    let cancelled = false
    signInCheck.user
      .getIdTokenResult()
      .then((result) => {
        if (!cancelled) {
          Sentry.setTag('impersonating', result.claims.impersonating === true)
        }
      })
      .catch(() => {
        if (!cancelled) Sentry.setTag('impersonating', null)
      })

    return () => {
      cancelled = true
      Sentry.setUser(null)
      Sentry.setTag('impersonating', null)
    }
    // Re-run only when the signed-in identity (uid) changes; impersonation
    // start/stop changes the uid, so the tag stays current without depending on
    // the (less stable) user object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signInCheck?.user?.uid])

  useEffect(() => {
    const onProfileChange = async () => {
      try {
        if (profileStatus === 'success' && !profile) {
          await api(functions, 'users/create')
        }

        if (!profile) {
          return
        }

        if (
          !signInCheck.user.emailVerified &&
          !profile.emailVerificationSent &&
          !emailVerificationSent
        ) {
          await sendVerificationEmail(
            firestore,
            functions,
            signInCheck.user,
            searchParams
          )
          emailVerificationSent = true
        }

        if (!profile.verifiedSetupDone && signInCheck.user.emailVerified) {
          await api(functions, 'users/verified')
        }
      } catch (error) {
        if (error?.code === 'functions/resource-exhausted') {
          // The auto-sent verification email hit Firebase Auth's rate limit.
          // Show the friendly "too many attempts" message and skip Sentry
          // reporting — expected, handled condition. CL-WEBAPP-JF.
          message.error(i18next.t('frontend:auth.tooManyAttempts'))
        } else {
          errorResponse(error)
        }
      }
    }

    // noinspection JSIgnoredPromiseFromCall
    onProfileChange()
  }, [
    profileStatus,
    profile,
    signInCheck.user,
    searchParams,
    functions,
    firestore,
  ])

  return !signInCheck.user.emailVerified ? <VerifyEmail /> : <App />
}
